import { useEffect, useState } from 'react';

export const DesktopPrompt = ({ localeText }: { localeText: any }) => {
    const [copied, setCopied] = useState(false);
    const [appStoreLink, setAppStoreLink] = useState('https://simplystock.co');

    useEffect(() => {
        const userAgent = navigator.userAgent || navigator.vendor || (window as any).opera;
        if (/android/i.test(userAgent)) {
            setAppStoreLink('https://play.google.com/store/apps/details?id=com.bhairavasoft.simply');
        } else if (/iPad|iPhone|iPod/.test(userAgent) && !(window as any).MSStream) {
            setAppStoreLink('https://apps.apple.com/in/app/inventory-invoices-simply/id6737231476');
        }
    }, []);

    const copyUrl = () => {
        navigator.clipboard.writeText('https://web.simplystock.co').then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        }).catch(() => { });
    };

    return (
        <div className="w-full h-full bg-[#EDEAE6] flex justify-center items-center overflow-auto p-6 font-['Sora',_sans-serif]">
            {/* Font Import */}
            <link href="https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600;700&family=JetBrains+Mono:wght@600&display=swap" rel="stylesheet" />

            <div className="max-w-[500px] w-full bg-white rounded-[32px] overflow-hidden shadow-[0_20px_50px_rgba(28,22,18,0.1)] border border-[#1c1612]/5">
                <div className="flex flex-col bg-white">
                    <div className="px-8 pt-12 pb-6 text-center flex flex-col items-center">
                        {/* Device Illustration */}
                        <div className="flex items-end justify-center gap-4 mb-10 scale-110">
                            <div className="flex flex-col items-center gap-2">
                                <div className="w-[44px] h-[72px] rounded-[9px] border-2 border-[#1a1612] bg-[#f5f1ee] relative flex flex-col items-center justify-center gap-[3px]">
                                    <div className="absolute top-[6px] w-[14px] h-[2px] rounded-[1px] bg-[#1a1612]"></div>
                                    <div className="flex flex-col gap-[3px] px-[5px] mt-2">
                                        <div className="h-[2px] rounded-[1px] bg-[#F4622A] w-[26px]"></div>
                                        <div className="h-[2px] rounded-[1px] bg-[#F4622A] w-[16px]"></div>
                                        <div className="h-[1px] rounded-[1px] bg-[#F4622A] w-[22px]"></div>
                                    </div>
                                    <div className="absolute bottom-[5px] w-2 h-2 rounded-full border-[1.5px] border-[#1a1612]"></div>
                                </div>
                                <div className="text-[9px] font-semibold text-[#1a1612]">Mobile</div>
                                <div className="text-[9px] bg-[#FEF0EA] text-[#c2410c] border border-[#fbd0bc] rounded-[20px] px-2 py-[1px] font-semibold">You</div>
                            </div>

                            <div className="flex flex-col items-center gap-[3px] pb-3">
                                <div className="w-[1px] h-4 bg-[#e0dbd4]"></div>
                                <div className="text-[8px] font-bold text-[#c8c0b8] tracking-[0.08em]">VS</div>
                                <div className="w-[1px] h-4 bg-[#e0dbd4]"></div>
                            </div>

                            <div className="flex flex-col items-center gap-2">
                                <div className="flex flex-col items-center">
                                    <div className="w-[72px] h-[50px] rounded-[5px] border-2 border-[#1a1612] bg-[#f5f1ee] relative">
                                        <div className="p-2 px-[5px] flex flex-col gap-[2px]">
                                            <div className="h-[2px] rounded-[1px] bg-[#e0dbd4] w-full"></div>
                                            <div className="h-[2px] rounded-[1px] bg-[#e0dbd4] w-[70%]"></div>
                                            <div className="grid grid-cols-2 gap-[2px] mt-[2px]">
                                                <div className="h-3 rounded-[1px] bg-[#e0dbd4]"></div>
                                                <div className="h-3 rounded-[1px] bg-[#e0dbd4]"></div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="w-5 h-[5px] rounded-b-[3px] bg-[#1a1612] mx-auto relative before:content-[''] before:absolute before:top-[5px] before:-left-[5px] before:w-[30px] before:h-[2px] before:bg-[#1a1612] before:rounded-[1px]"></div>
                                </div>
                                <div className="text-[9px] font-semibold text-[#1a1612]">Desktop</div>
                                <div className="text-[9px] bg-[#f5f1ee] text-[#a09890] border border-[#e8e3dc] rounded-[20px] px-2 py-[1px] font-semibold whitespace-nowrap">Full power</div>
                            </div>
                        </div>

                        <div className="text-[26px] font-bold text-[#1a1612] tracking-[-0.03em] leading-[1.15] mb-3">
                            Right tool,<br />right <em className="not-italic text-[#F4622A]">device</em>
                        </div>
                        <div className="text-[14px] text-[#a09890] leading-relaxed max-w-[320px]">
                            Use the app on your phone.<br />Open the web for setup & reports.
                        </div>
                    </div>

                    {/* Web URL Area */}
                    <div className="px-8 pt-6 pb-2">
                        <div className="flex items-center gap-3 mb-5">
                            <div className="flex-1 h-[1px] bg-[#f0ece8]"></div>
                            <div className="text-[11px] text-[#c8c0b8] font-bold whitespace-nowrap uppercase tracking-widest">For desktop / laptop</div>
                            <div className="flex-1 h-[1px] bg-[#f0ece8]"></div>
                        </div>
                        <div className="bg-[#f5f1ee] rounded-2xl p-4 flex items-center gap-3 border border-[#1c1612]/5">
                            <div className="w-9 h-9 rounded-xl bg-white border border-[#e8e3dc] flex items-center justify-center flex-shrink-0 shadow-sm">
                                <svg width="16" height="16" viewBox="0 0 13 13" fill="none">
                                    <circle cx="6.5" cy="6.5" r="5.5" stroke="#a09890" strokeWidth="1" />
                                    <path d="M6.5 1C6.5 1 4.5 3.5 4.5 6.5S6.5 12 6.5 12M6.5 1C6.5 1 8.5 3.5 8.5 6.5S6.5 12 6.5 12M1 6.5h11" stroke="#a09890" strokeWidth="1" />
                                </svg>
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="text-[10px] font-bold uppercase tracking-[0.08em] text-[#c8c0b8] mb-0.5">Web app</div>
                                <div className="text-[14px] font-bold text-[#1a1612] font-mono leading-none truncate">web.simplystock.co</div>
                            </div>
                            <button
                                onClick={copyUrl}
                                className={`px-4 py-2 rounded-xl border-none font-['Sora',_sans-serif] text-[12px] font-bold text-white cursor-pointer flex-shrink-0 transition-all active:scale-95 whitespace-nowrap shadow-sm ${copied ? 'bg-[#16a34a]' : 'bg-[#1a1612]'}`}
                            >
                                {copied ? 'Copied!' : 'Copy'}
                            </button>
                        </div>
                    </div>

                    {/* CTA Area */}
                    <div className="px-8 pt-6 pb-12">
                        <a
                            href={appStoreLink}
                            target="_blank"
                            rel="noreferrer"
                            className="w-full py-4.5 rounded-[18px] bg-[#F4622A] text-white font-bold text-[16px] flex items-center justify-center gap-2.5 tracking-[-0.01em] transition-all active:scale-[0.98] hover:bg-[#d94f1e] no-underline shadow-lg shadow-[#F4622A]/20"
                        >
                            Continue with app
                            <svg width="15" height="15" viewBox="0 0 13 13" fill="none">
                                <path d="M3 6.5h7M7 3.5l3 3-3 3" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        </a>
                    </div>
                </div>
            </div>
        </div>
    );
};

